const acorn = require('acorn');
const TYPES = new Set(['button', 'toggle', 'text', 'navigation', 'export']);
const KEY_NAMES = { 13: 'Enter', 27: 'Escape', 32: ' ', 37: 'ArrowLeft', 38: 'ArrowUp', 39: 'ArrowRight', 40: 'ArrowDown' };
const allowedKey = key => typeof key === 'string'
  && (/^[A-Za-z0-9 ]$/.test(key) || Object.values(KEY_NAMES).includes(key));
const idPattern = /^[a-z][a-z0-9-]{0,39}$/;

// An artist opts in through p.immutablesControls in the posted script. Only
// plain labels and supported control types reach React; never HTML or eval.
function declaredProfile(declaration) {
  if (!declaration || declaration.version !== 1 || !Array.isArray(declaration.actions)
    || declaration.actions.length > 12) return null;
  const actions = [], exports = [], ids = new Set();
  for (const item of declaration.actions) {
    if (!item || !idPattern.test(item.id) || ids.has(item.id) || !TYPES.has(item.type)
      || typeof item.label !== 'string' || !item.label.trim() || item.label.length > 48) return null;
    if (item.key !== undefined && !allowedKey(item.key)) return null;
    if ((!item.key || ['text', 'navigation'].includes(item.type)) && typeof declaration.onAction !== 'function') return null;
    if (item.type === 'toggle' && typeof declaration.getState !== 'function') return null;
    if (item.type === 'text' && item.maxLength !== undefined
      && (!Number.isInteger(item.maxLength) || item.maxLength < 1 || item.maxLength > 500)) return null;
    const action = { id: item.id, label: item.label.trim(), type: item.type,
      key: item.key, maxLength: item.maxLength || 120 };
    ids.add(action.id);
    (action.type === 'export' ? exports : actions).push(action);
  }
  return { source: 'declared', actions, exports };
}

// Conservative discovery for older, unannotated p5 scripts. Read syntax only;
// comments and quoted examples are not bindings. Parsing newer syntax that this
// parser cannot understand simply leaves the explicit runtime convention available.
function discoverKeys(script) {
  let ast;
  try { ast = acorn.parse(script, { ecmaVersion: 2020, allowReturnOutsideFunction: true }); }
  catch (error) { return []; }
  const keys = new Map();
  const property = node => node && node.type === 'MemberExpression' && !node.computed && node.property.name;
  function walk(node, handler) {
    if (!node || typeof node !== 'object') return;
    if (node.type === 'AssignmentExpression' && ['keyTyped', 'keyPressed'].includes(property(node.left))
      && node.left.object.type === 'Identifier' && node.left.object.name === 'p') handler = property(node.left);
    if (handler && node.type === 'BinaryExpression' && ['==', '==='].includes(node.operator)) {
      const pair = [[node.left, node.right], [node.right, node.left]];
      for (const [member, literal] of pair) {
        if (!['key', 'keyCode'].includes(property(member)) || member.object.name !== 'p' || literal.type !== 'Literal') continue;
        let key = literal.value;
        if (property(member) === 'keyCode') key = KEY_NAMES[key] || (key >= 48 && key <= 90 ? String.fromCharCode(key) : null);
        if (allowedKey(key)) keys.set(key, { key, handler });
      }
    }
    for (const value of Object.values(node)) {
      if (Array.isArray(value)) value.forEach(child => walk(child, handler));
      else if (value && typeof value === 'object') walk(value, handler);
    }
  }
  walk(ast, null);
  return [...keys.values()];
}

function discoveredProfile(script) {
  const keys = discoverKeys(script);
  // S/P commonly start expensive exports; Enter/Escape/Backspace are contextual.
  // An explicit declaration can expose them with a meaningful label and behavior.
  const usable = keys.filter(({ key }) => !['S', 'P', 'ENTER', 'ESCAPE', 'BACKSPACE'].includes(key.toUpperCase()));
  const seen = new Set();
  const actions = usable.filter(({ key }) => {
    const upper = key.toUpperCase();
    if (seen.has(upper)) return false;
    seen.add(upper); return true;
  }).slice(0, 12).map(({ key }, i) => ({ id: `key-${i}`, label: key === ' ' ? 'Space' : `Key ${key.toUpperCase()}`, type: 'button', key }));
  return { source: 'detected', actions, exports: [] };
}

module.exports = { declaredProfile, discoverKeys, discoveredProfile };
